// src/SuperSettings.js
import React, { useEffect, useState } from 'react';
import API from './api';

/**
 * SuperSettings
 *  - NE dira per-organization system_settings.
 *  - Sve “operativne” postavke ostaju na nivou organizacije (org admin).
 *  - Ovo je SaaS-level ekran: defaulti i guardrails za nove organizacije
 *    + definicija planova + feature matrix.
 */

const planOptions = ['trial', 'basic', 'pro', 'enterprise'];
const scopeOptions = ['global', 'admin', 'teacher', 'parent', 'super_admin'];

const SuperSettings = () => {
  // ----- POSTOJEĆE: defaults + flags -----
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [defaults, setDefaults] = useState({
    defaultBillingPlan: 'basic',
    defaultBillingCycle: 'monthly',
    defaultCurrency: 'USD',
    trialDays: 30,
    maxTeachersPerOrg: 200,
    maxStudentsPerOrg: 3000,
  });

  const [flags, setFlags] = useState({
    allowNewOrgSignup: false,
    autoCreateTrialOrg: true,
    sendSystemEmailsToSuperAdmin: true,
  });

  // ----- NOVO: saas_plans -----
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansSaving, setPlansSaving] = useState(false);
  const [plansMsg, setPlansMsg] = useState('');
  const [plansError, setPlansError] = useState('');

  // ----- NOVO: features matrix -----
  const [featureRows, setFeatureRows] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState(false);
  const [featuresMsg, setFeaturesMsg] = useState('');
  const [featuresError, setFeaturesError] = useState('');

  // Učitavanje iz BE (defaults + flags)
  useEffect(() => {
    let isMounted = true;

    async function fetchSettings() {
      setLoading(true);
      setLoadError('');
      try {
        const res = await API.get('/super/settings');
        if (!isMounted) return;

        const data = res.data || {};
        if (data.defaults) setDefaults(data.defaults);
        if (data.flags) setFlags(data.flags);
      } catch (err) {
        console.error('Failed to load super settings', err);
        if (isMounted) {
          setLoadError('Failed to load settings. Please try again.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  // Učitavanje planova
  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      setPlansError('');
      try {
        const res = await API.get('/super/plans');
        setPlans(res.data || []);
      } catch (err) {
        console.error('Failed to load plans', err);
        setPlansError('Failed to load SaaS plans.');
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Učitavanje features
  useEffect(() => {
    const fetchFeatures = async () => {
      setFeaturesLoading(true);
      setFeaturesError('');
      try {
        const res = await API.get('/super/features');
        const list = res.data || [];
        setFeatureRows(list);
      } catch (err) {
        console.error('Failed to load features', err);
        setFeaturesError('Failed to load features.');
      } finally {
        setFeaturesLoading(false);
      }
    };
    fetchFeatures();
  }, []);

  // --- HANDLERS za defaults/flags ---
  const handleDefaultsChange = (field, value) => {
    setDefaults((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleFlagsChange = (field, value) => {
    setFlags((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      await API.post('/super/settings', {
        defaults,
        flags,
      });

      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to save super settings', err);
      setSaveError('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // --- HANDLERS za planove ---
  const handlePlanFieldChange = (index, field, value) => {
    setPlans((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
    setPlansMsg('');
    setPlansError('');
  };

  const handleSavePlans = async () => {
    setPlansSaving(true);
    setPlansMsg('');
    setPlansError('');
    try {
      await API.put('/super/plans', plans);
      setPlansMsg('Plans saved successfully.');
    } catch (err) {
      console.error('Failed to save plans', err);
      setPlansError('Error saving plans. Please try again.');
    } finally {
      setPlansSaving(false);
    }
  };

  // --- HANDLERS za feature matrix ---
  const handleFeatureFieldChange = (index, field, value) => {
    setFeatureRows((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
    setFeaturesMsg('');
    setFeaturesError('');
  };

  const handleSaveFeatures = async () => {
    setFeaturesSaving(true);
    setFeaturesMsg('');
    setFeaturesError('');
    try {
      await API.put('/super/features', featureRows);
      setFeaturesMsg('Features saved successfully.');
    } catch (err) {
      console.error('Failed to save features', err);
      setFeaturesError('Error saving features. Please try again.');
    } finally {
      setFeaturesSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-3 text-sm text-gray-700">
        <p>Loading super admin settings…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-3">
        <h1 className="text-base font-semibold mb-1">
          Super Admin – Global Settings & Plans
        </h1>
        <p className="text-xs text-gray-600 leading-relaxed">
          All operational settings (emails, timezone, reminders, templates, etc.)
          are owned by each organization individually via the{' '}
          <strong>system_settings</strong> table. Organization admins manage
          those under their own <em>Settings</em> page.
        </p>
        <p className="text-xs text-gray-600 mt-2">
          This page defines <strong>SaaS-level defaults</strong>, plan limits
          and <strong>feature availability</strong> across plans. It does not
          override per-organization configuration, but it drives what new
          tenants get by default.
        </p>
      </div>

      {/* Error pri učitavanju, ako treba */}
      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-3 py-2">
          {loadError}
        </div>
      )}

      {/* SaaS defaults */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm p-3 space-y-4 text-sm text-gray-800"
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold">Defaults for new organizations</h2>
          {saveSuccess && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
              Saved successfully
            </span>
          )}
        </div>

        {saveError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-2 py-1 mb-2">
            {saveError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Default billing plan */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Default billing plan for new orgs
            </label>
            <select
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={defaults.defaultBillingPlan}
              onChange={(e) =>
                handleDefaultsChange('defaultBillingPlan', e.target.value)
              }
            >
              <option value="trial">Trial</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <p className="text-[11px] text-gray-500">
              Used only when you create a new organization. It can always be
              changed per organization.
            </p>
          </div>

          {/* Default billing cycle */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Default billing cycle
            </label>
            <select
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={defaults.defaultBillingCycle}
              onChange={(e) =>
                handleDefaultsChange('defaultBillingCycle', e.target.value)
              }
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
            <p className="text-[11px] text-gray-500">
              This is a suggestion when setting up a new org; the final value
              lives in <code>organizations.billing_cycle</code>.
            </p>
          </div>

          {/* Default currency */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Default currency
            </label>
            <select
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={defaults.defaultCurrency}
              onChange={(e) =>
                handleDefaultsChange('defaultCurrency', e.target.value)
              }
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
            <p className="text-[11px] text-gray-500">
              Used for new organizations; the actual currency is stored in{' '}
              <code>organizations.currency</code>.
            </p>
          </div>

          {/* Trial days */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Default trial length (days)
            </label>
            <input
              type="number"
              min={0}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={defaults.trialDays}
              onChange={(e) =>
                handleDefaultsChange('trialDays', Number(e.target.value) || 0)
              }
            />
            <p className="text-[11px] text-gray-500">
              When creating a trial organization, you can use this as the
              default offset for <code>trial_end_date</code>.
            </p>
          </div>

          {/* Max teachers per org */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Soft limit — max teachers per organization
            </label>
            <input
              type="number"
              min={0}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={defaults.maxTeachersPerOrg}
              onChange={(e) =>
                handleDefaultsChange(
                  'maxTeachersPerOrg',
                  Number(e.target.value) || 0
                )
              }
            />
            <p className="text-[11px] text-gray-500">
              Later you can use this as a guard when creating users with
              role=teacher for a given org.
            </p>
          </div>

          {/* Max students per org */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-700">
              Soft limit — max students per organization
            </label>
            <input
              type="number"
              min={0}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={defaults.maxStudentsPerOrg}
              onChange={(e) =>
                handleDefaultsChange(
                  'maxStudentsPerOrg',
                  Number(e.target.value) || 0
                )
              }
            />
            <p className="text-[11px] text-gray-500">
              Similar guardrail for <code>students</code> count in a single
              organization.
            </p>
          </div>
        </div>

        {/* Global flags */}
        <div className="pt-3 border-t border-gray-200">
          <h2 className="text-sm font-semibold mb-2">Global SaaS flags</h2>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="rounded"
                checked={flags.allowNewOrgSignup}
                onChange={(e) =>
                  handleFlagsChange('allowNewOrgSignup', e.target.checked)
                }
              />
              <span>
                Allow public sign-up for new organizations (self-service trial)
                <span className="block text-[11px] text-gray-500">
                  If disabled, only super admin can create new organizations
                  from the Super panel.
                </span>
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="rounded"
                checked={flags.autoCreateTrialOrg}
                onChange={(e) =>
                  handleFlagsChange('autoCreateTrialOrg', e.target.checked)
                }
              />
              <span>
                Auto-create trial orgs with default limits
                <span className="block text-[11px] text-gray-500">
                  Controls whether onboarding flow should auto-provision a trial
                  using defaults above.
                </span>
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                className="rounded"
                checked={flags.sendSystemEmailsToSuperAdmin}
                onChange={(e) =>
                  handleFlagsChange(
                    'sendSystemEmailsToSuperAdmin',
                    e.target.checked
                  )
                }
              />
              <span>
                Send system-level alerts to super admin email
                <span className="block text-[11px] text-gray-500">
                  For example: billing job failures, cron errors, or critical
                  org notifications.
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Save button */}
        <div className="pt-3 border-t border-gray-200 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-3 py-1.5 text-xs rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save super settings'}
          </button>
        </div>
      </form>

      {/* SaaS plans & limits */}
      <div className="bg-white rounded-xl shadow-sm p-3 text-sm text-gray-800">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold">SaaS plans & limits</h2>
            <p className="text-[11px] text-gray-500">
              Plan definitions used when you select <code>billing_plan</code> in
              the Super Organizations panel.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSavePlans}
            disabled={plansSaving}
            className="px-3 py-1.5 text-[11px] rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {plansSaving ? 'Saving…' : 'Save plans'}
          </button>
        </div>

        {plansError && (
          <div className="mb-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded px-2 py-1">
            {plansError}
          </div>
        )}
        {plansMsg && (
          <div className="mb-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded px-2 py-1">
            {plansMsg}
          </div>
        )}

        {plansLoading ? (
          <p className="text-xs text-gray-500">Loading plans…</p>
        ) : (
          <div className="overflow-x-auto text-xs">
            <table className="min-w-full border border-gray-100 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-1 px-2 border-b">Key</th>
                  <th className="text-left py-1 px-2 border-b">Name</th>
                  <th className="text-left py-1 px-2 border-b">Rank</th>
                  <th className="text-left py-1 px-2 border-b">Max teachers</th>
                  <th className="text-left py-1 px-2 border-b">Max students</th>
                  <th className="text-left py-1 px-2 border-b">
                    Max monthly visits
                  </th>
                  <th className="text-left py-1 px-2 border-b">Base fee</th>
                  <th className="text-left py-1 px-2 border-b">
                    Price / teacher
                  </th>
                  <th className="text-left py-1 px-2 border-b">
                    Price / student
                  </th>
                  <th className="text-left py-1 px-2 border-b">Active</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p, index) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-1 px-2 align-middle">
                      <span className="font-mono text-[11px] bg-gray-50 px-1 py-0.5 rounded">
                        {p.plan_key}
                      </span>
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded px-1 py-0.5"
                        value={p.plan_name || ''}
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'plan_name',
                            e.target.value
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        className="w-16 border border-gray-200 rounded px-1 py-0.5"
                        value={p.plan_rank ?? 0}
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'plan_rank',
                            Number(e.target.value) || 0
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        className="w-20 border border-gray-200 rounded px-1 py-0.5"
                        value={
                          p.default_max_teachers != null
                            ? p.default_max_teachers
                            : ''
                        }
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'default_max_teachers',
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        className="w-20 border border-gray-200 rounded px-1 py-0.5"
                        value={
                          p.default_max_students != null
                            ? p.default_max_students
                            : ''
                        }
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'default_max_students',
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        className="w-24 border border-gray-200 rounded px-1 py-0.5"
                        value={
                          p.default_max_monthly_visits != null
                            ? p.default_max_monthly_visits
                            : ''
                        }
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'default_max_monthly_visits',
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        step="0.01"
                        className="w-24 border border-gray-200 rounded px-1 py-0.5"
                        value={
                          p.default_base_fee != null ? p.default_base_fee : ''
                        }
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'default_base_fee',
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        step="0.01"
                        className="w-24 border border-gray-200 rounded px-1 py-0.5"
                        value={
                          p.default_price_per_teacher != null
                            ? p.default_price_per_teacher
                            : ''
                        }
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'default_price_per_teacher',
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="number"
                        step="0.01"
                        className="w-24 border border-gray-200 rounded px-1 py-0.5"
                        value={
                          p.default_price_per_student != null
                            ? p.default_price_per_student
                            : ''
                        }
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'default_price_per_student',
                            e.target.value === '' ? null : Number(e.target.value)
                          )
                        }
                      />
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="checkbox"
                        className="h-3 w-3"
                        checked={!!p.is_active}
                        onChange={(e) =>
                          handlePlanFieldChange(
                            index,
                            'is_active',
                            e.target.checked
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feature matrix */}
      <div className="bg-white rounded-xl shadow-sm p-3 text-sm text-gray-800">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold">Features & plan levels</h2>
            <p className="text-[11px] text-gray-500">
              For each feature you can adjust the minimum plan and its scope.
              <br />
              <strong>Default behavior:</strong> if{' '}
              <code>min_plan = basic</code>, all orgs with{' '}
              <code>billing_plan</code> ≥ basic get it, unless it is overridden
              via <code>organization_features</code>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSaveFeatures}
            disabled={featuresSaving}
            className="px-3 py-1.5 text-[11px] rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {featuresSaving ? 'Saving…' : 'Save features'}
          </button>
        </div>

        {featuresError && (
          <div className="mb-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded px-2 py-1">
            {featuresError}
          </div>
        )}
        {featuresMsg && (
          <div className="mb-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded px-2 py-1">
            {featuresMsg}
          </div>
        )}

        {featuresLoading ? (
          <p className="text-xs text-gray-500">Loading features…</p>
        ) : (
          <div className="overflow-x-auto text-xs">
            <table className="min-w-full border border-gray-100 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-1 px-2 border-b">Name</th>
                  <th className="text-left py-1 px-2 border-b">Key</th>
                  <th className="text-left py-1 px-2 border-b">Scope</th>
                  <th className="text-left py-1 px-2 border-b">Min plan</th>
                  <th className="text-left py-1 px-2 border-b">Enabled by default</th>
                </tr>
              </thead>
              <tbody>
                {featureRows.map((f, index) => (
                  <tr key={f.feature_key} className="border-b last:border-0">
                    <td className="py-1 px-2 align-middle">
                      <div className="font-semibold text-gray-800">
                        {f.name || f.feature_key}
                      </div>
                      {f.description && (
                        <div className="text-[10px] text-gray-500">
                          {f.description}
                        </div>
                      )}
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <span className="font-mono text-[11px] bg-gray-50 px-1 py-0.5 rounded">
                        {f.feature_key}
                      </span>
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <select
                        className="border border-gray-200 rounded px-1 py-0.5"
                        value={f.scope || 'admin'}
                        onChange={(e) =>
                          handleFeatureFieldChange(
                            index,
                            'scope',
                            e.target.value
                          )
                        }
                      >
                        {scopeOptions.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <select
                        className="border border-gray-200 rounded px-1 py-0.5"
                        value={f.min_plan || 'basic'}
                        onChange={(e) =>
                          handleFeatureFieldChange(
                            index,
                            'min_plan',
                            e.target.value
                          )
                        }
                      >
                        {planOptions.map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1 px-2 align-middle">
                      <input
                        type="checkbox"
                        className="h-3 w-3"
                        checked={!!f.default_enabled}
                        onChange={(e) =>
                          handleFeatureFieldChange(
                            index,
                            'default_enabled',
                            e.target.checked
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info card */}
      <div className="bg-white rounded-xl shadow-sm p-3 text-xs text-gray-700">
        <h2 className="text-sm font-semibold mb-1">
          How this fits into the multi-tenant model
        </h2>
        <ul className="list-disc list-inside space-y-1 mt-1">
          <li>
            Each tenant (agency) lives in the <code>organizations</code> table,
            with its own billing plan, limits and currency.
          </li>
          <li>
            Per-organization settings (emails, templates, parent notifications,
            etc.) live in <code>system_settings</code> and are editable by the
            org admin.
          </li>
          <li>
            Super admin uses this page to define <strong>defaults</strong>,{' '}
            <strong>plan limits</strong> and feature availability. Feature
            flags per user are still resolved at runtime using{' '}
            <code>features.min_plan</code>, <code>organization_features</code>{' '}
            and <code>organizations.billing_plan</code>.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SuperSettings;

